import type { BuildingDef, BuildingRules, Lift } from '@full-elevator/sim';
import { liftProblems } from '@full-elevator/sim';
import officeTower from '../buildings/01-office-tower.json' with { type: 'json' };
import grandHotel from '../buildings/02-grand-hotel.json' with { type: 'json' };
import transitCentre from '../buildings/03-transit-centre.json' with { type: 'json' };
import skylineTower from '../buildings/04-skyline-tower.json' with { type: 'json' };

/**
 * The buildings, in the order the player meets them.
 *
 * A building is data, exactly like a level: an id, a sentence about what it does
 * to the traffic problem, and a bundle of structural rules from the sim's
 * general vocabulary. There is no per-building code anywhere, and if a fifth one
 * ever needs some, the rule it needs belongs in the sim as a general rule first.
 *
 * The list is explicit rather than glob-loaded so the order is data you can
 * read, and so a building cannot join the game by existing on disk.
 */
export const BUILDINGS: readonly BuildingDef[] = [
  officeTower,
  grandHotel,
  transitCentre,
  skylineTower,
] as BuildingDef[];

export const BUILDING_IDS: readonly string[] = BUILDINGS.map((b) => b.id);

// A building's lift is held to the same rules as the standard one — named
// parts, real numbers, steps you feel, never a percentage — and checked here,
// at load, so a bad catalogue never reaches a player.
for (const building of BUILDINGS) {
  const problems = building.lift ? liftProblems(building.lift) : [];
  // The street is somewhere people come in (ADR-0028): a ground floor with no
  // door would be a building nobody could enter from outside.
  const ground = building.groundFloor ?? 0;
  if (!(building.rules.entrances ?? [0]).includes(ground)) {
    problems.push(`groundFloor ${ground} is not one of its entrances`);
  }
  if (problems.length > 0) {
    throw new Error(
      `Building "${building.id}" has a lift that will not do:\n  ${problems.join('\n  ')}`,
    );
  }
}

export function getBuilding(id: string): BuildingDef {
  const found = BUILDINGS.find((b) => b.id === id);
  if (!found) {
    throw new Error(`Unknown building "${id}". Known: ${BUILDING_IDS.join(', ')}`);
  }
  return found;
}

/** The rules for a building id, or none at all when a level names no building. */
export function rulesFor(id: string | undefined): BuildingRules {
  return id === undefined ? {} : getBuilding(id).rules;
}

/**
 * A building's own lift (ADR-0027), or undefined for the standard one — which
 * is also what a level naming no building is played on.
 */
export function liftFor(id: string | undefined): Lift | undefined {
  if (id === undefined) return undefined;
  const parts = getBuilding(id).lift;
  return parts ? { id, parts } : undefined;
}
